import { useEffect } from "react";
import {
  useIsEditor,
  useIsReadOnly,
  usePreviewDefaults,
} from "../DocumentView/DocumentContext";

/**
 * Returns the effective value an input should display:
 *
 * - In a fillable form (`!isEditor`): writes the configured default into
 *   the element's value on first render if nothing is set, and returns
 *   the default until the write lands. The returned value also avoids a
 *   brief empty-state flicker between mount and useEffect.
 * - In the editor: never writes (so designing a form doesn't dirty
 *   filler state) but still returns the default so the designer's
 *   preview shows what the filler will see.
 * - Read-only (viewing a submitted or draft response): never writes and
 *   returns only the persisted value, so a field the filler never
 *   reached shows as unanswered rather than as its configured default.
 *   A read-only preview of a blank form (DocumentView `previewDefaults`)
 *   still returns the default, without writing.
 *
 * Effect is mount-only by design — re-running on prop changes would
 * re-hydrate after the filler edits or clears, which is wrong.
 */
export function useDefaultValue<T>(
  value: T | undefined,
  setValue: (v: T) => void,
  defaultValue: T | undefined,
): T | undefined {
  const isEditor = useIsEditor();
  const isReadOnly = useIsReadOnly();
  const previewDefaults = usePreviewDefaults();
  useEffect(() => {
    if (isEditor || isReadOnly) return;
    if (value !== undefined) return;
    if (defaultValue === undefined) return;
    if (typeof defaultValue === "string" && defaultValue === "") return;
    setValue(defaultValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (isReadOnly && !previewDefaults) return value;
  return value !== undefined ? value : defaultValue;
}
